import React, { Component } from 'react'
import { NavLink, Route, RouteComponentProps, Switch } from 'react-router-dom'
import { AxiosInstance } from 'axios'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Meta, RatatoskRequest, getLocalizedLabel } from '../../common'
import { Button, Checkbox, Container, Dimmer, Dropdown, Grid, Header, Icon, Input, Label, Loader, Message, Modal, ModalContent, Progress, Segment } from 'semantic-ui-react'
import { Md5 } from 'ts-md5' 
import { addForms, createParadigm, filterPatterns, response2groups } from './CNCDictSublemma'
import { batchByTag, checkLemma, lemmaPresent } from './CNCDictBatchChange'
import './CNCDictCandidatesItem.css'

interface CNCDictCandidatesItemProps extends WithTranslation, RouteComponentProps {
    meta: Meta,
    http: AxiosInstance
    patternList: any[]
    itemIdx: number,
    sublemmas?: any[], // for AddSublemmas
    candidates?: any[], // for Candidates
    candIdx?: number, // for Candidates
    setSubLemma: (i: number, subi: number, value: string) => void,
    setPattern: (i: number, subi: number, value: string) => void,
    toggleSelect?: (i: number) => void, // for Candidates
    setCreated: (i: number) => void,
    nextUnknown?: (formList: string[]) => void // for Candidates
}

interface CNCDictCandidatesItemState {
    savingForms: boolean,
    loadingHomonyms: boolean,
    errorMsg: string,
    homonyms: any[] | null,
    homonymsFor: string,
    filters: string[][],
    lemma: string[],
    lemmaDialogIndex: number,
    newLemma: string,
    testingLemma: boolean,
    existingLemma: lemmaPresent
}

class CNCDictCandidatesItem extends Component<CNCDictCandidatesItemProps, CNCDictCandidatesItemState> {

    state = {
        savingForms: false,
        loadingHomonyms: false,
        errorMsg: '',
        homonyms: null as any[]|null,
        homonymsFor: '',
        filters: [[],[]] as string[][], // currently, we don't expect more than two patterns per item
        lemma: ['', ''], // currently, we don't expect more than two patterns per item
        newLemma: '',
        lemmaDialogIndex: -1,
        testingLemma: false,
        existingLemma: lemmaPresent.unknown
    }

    async componentDidMount(): Promise<void> {
    }

    componentDidUpdate(prevProps: Readonly<CNCDictCandidatesItemProps>, prevState: Readonly<CNCDictCandidatesItemState>, snapshot?: any): void {
    }

    groupHomonyms(data) {
        const groups = data.reduce((acc, u: any) => {
            const form = u._slots[0]._fillers[0][':form:attr:cnc:w:word']
            if (!acc[form]) acc[form] = []
            acc[form].push(u)
            return acc
        }, [])
        return Object.keys(groups)
    }

    loadHomonyms(formList: string[], formsId) {
        let request: RatatoskRequest = {
            query: {
                type: ':cncdict:form',
                feats: [
                    {type: ':form:attr:cnc:w:word', value: formList, ci: true}
                ]
            },
            page: { from: 0, size: 10000 },
            feats: [':form:attr:cnc:w', ':form:cncdict:domain']
        }
        this.setState({loadingHomonyms: true})
        const url = 'cunits/_view'
        this.props.http.post(url, request)
            .then(response => {
                const homonyms = this.groupHomonyms(response.data.data)
                this.setState({homonyms: homonyms, errorMsg: '', loadingHomonyms: false, homonymsFor: formsId})
            }).catch(err => {
                this.setState({loadingHomonyms: false})
                console.log(err)
                if (err.response) {
                    this.setState({errorMsg: err.response.data.message, loadingHomonyms: false, homonymsFor: formsId})
                }
            })
    }

    async updateNewLemma(form: string) {
        const { http } = this.props
        this.setState({newLemma: form, testingLemma: true})
        const res = await checkLemma(http, form)
        if (res) {
            this.setState({testingLemma: false, existingLemma: lemmaPresent.asLemma})
        } else if (res===false) {
            const res = await checkLemma(http, form, true)
            if (res) {
                this.setState({testingLemma: false, existingLemma: lemmaPresent.asSublemma})
            } else if (res===false) {
                this.setState({testingLemma: false, existingLemma: lemmaPresent.none})
            } else {
                this.setState({testingLemma: false, existingLemma: lemmaPresent.unknown})
            }
        } else {
            this.setState({testingLemma: false, existingLemma: lemmaPresent.unknown})
        }
    }

    defaultFilters(sublemma, pattern) {
        const pos = pattern.substring(0,1)
        if (pos==='N' && /\p{Lu}/u.test(sublemma.substring(0,1)) && !/N[IMFN]P.*/.test(pattern)) {
            return ['+singular']
        }
        if (/N[IMFN]P.*/.test(pattern)) return []
        return null
    }

    render() {
        const { patternList, sublemmas, candidates, candIdx, itemIdx, setSubLemma, setPattern, toggleSelect, setCreated, nextUnknown, http, i18n, t, meta, history } = this.props
        const { savingForms, errorMsg, loadingHomonyms, homonyms, filters, lemmaDialogIndex, lemma, newLemma, testingLemma, existingLemma } = this.state
        
        if (!((candidates && candIdx) || sublemmas)) return null

        const item = candidates && candIdx ? candidates[candIdx].patterns[itemIdx] : sublemmas![itemIdx]
        const noneSelected = candidates && candIdx ? !candidates[candIdx].patterns.some(x => x.selected) : true
        const itemActiveButton = item.selected || noneSelected
        const myPatterns = item.sublemma.map(sublemma => filterPatterns(patternList, sublemma, item.patternFilter||''))
        if (item.fromFile && !item.pattern[0] && myPatterns[0].length) {
            const pattern = myPatterns[0][0]._name
            setPattern(itemIdx, 0, pattern)
            const defaultFilters = this.defaultFilters(item.sublemma[0], pattern)
            if (defaultFilters) {
                this.setState({filters: [defaultFilters, []]})
            }
        }
        const patternOptions = item.sublemma.map((sublemma, i) => myPatterns[i].map((p:any) => ({key: p._name, value: p._name, text: p._name})))
        const curPattern = item.pattern.map((name: string) => {
            const found = patternList.filter(p => name === p._name)
            return found.length ? found[0] : null
        })

        const pos = item.pattern.map(p => p.substring(0,1))
        const filterConfig = item.sublemma.map((l, i) => batchByTag[pos[i]] || {})
        const filterChoice = item.sublemma.map((l, i) => pos[i] === 'N' ? ['+singular'] : [])
        const filterCheckboxes = item.sublemma.map((l, i) => filterChoice[i].map((o: string) => {
                const name = o.substring(1)
                return <><Checkbox checked={filters[i].indexOf(o)>=0} onClick={() => toggleFilter(i, o)} className="inline filter"/> {(o.startsWith('+') ? '' : t("-"))+" "+t(['abbr_'+name, name])}</>
            })
        )
        const filterTags = filters.map((fl, i) => fl.reduce((acc: any, f: string) => {
                const name = f.substring(1)
                if (f.startsWith('+'))
                    acc['include'].push(filterConfig[i][name])
                else
                    acc['exclude'].push(filterConfig[i][name])
                return acc
            }, {include: [], exclude: []})
        )

        const forms = item.sublemma.filter(i => i).length && curPattern.length ? 
            item.sublemma.reduce((acc: any[], sublemma: string, i: number) => sublemma && curPattern[i] ? acc.concat(createParadigm(sublemma, lemma[i]||sublemma, curPattern[i], filterTags[i]['include'], filterTags[i]['exclude'])) : acc, []) 
            : []
        const formNames = forms.reduce((acc: string[], f: any) => {
            if (!acc.includes(f.form)) {
                acc.push(f.form)
            }
            return acc
        }, [])
        const formSum = Md5.hashStr(JSON.stringify(formNames))
        if (!loadingHomonyms && formNames.length && formSum !== this.state.homonymsFor)
            this.loadHomonyms(formNames, formSum)
        
        const saveForms = async (forms: any[]) => {
            let errors: string[] = []
            this.setState({savingForms: true})
            const formList: any[] = []
            const wordList: string[] = []
            for (let unit of forms) {
                let domain: string[] = []
                if (unit.domain.written) domain.push('written')
                if (unit.domain.spoken) domain.push('spoken')
                formList.push({sublemma: unit.sublemma, lemma: unit.lemma, word: unit.form, tag: unit.tag, domain: domain})
                wordList.push(unit.form)
            }
            const res = await addForms(http, formList)
            if (typeof res === 'string') errors.push(res)
            else if (!res.length) errors.push(i18n.t("Empty response."))
            else {
                for (let i of res) {
                    if (i._error) errors.push(i._error)
                }
            }
            this.setState({savingForms: false, errorMsg: errors.join("\n")})
            setCreated(itemIdx)
            if (noneSelected && nextUnknown)
                nextUnknown(wordList)
        }

        const toggleFilter = (idx, name) => {
            if (filters[idx].indexOf(name)>=0)
                this.setState({filters: filters.map((f, i) => (idx===i ? f.filter(x => x!==name) : f))})
            else
                this.setState({filters: filters.map((f, i) => (idx===i ? [...f, name] : f))})
        }

        const setLemma = (index: number, value: string) => {
            const {lemma} = this.state
            lemma.splice(index, 1, value);console.log(lemma)
            this.setState({lemma: lemma, lemmaDialogIndex: -1, existingLemma: lemmaPresent.unknown})
        }

        const openLemmaDialog = (index: number, value: string) => {
            this.setState({lemmaDialogIndex: index, newLemma: value, existingLemma: lemmaPresent.unknown})
            this.updateNewLemma(value)
        }

        const coveredForms = item.original || !candidates ? item.forms : candidates.filter(c => forms.filter(f => f.form.toLowerCase() === c.form.toLowerCase()).length)
        const coverage = candidates ?
                            item.original ? item.fq : coveredForms.reduce((acc, i) => acc+i.fq, 0)
                        : 0

        return(
            <>
            <Modal size="tiny" open={lemmaDialogIndex>=0} centered={false} closeIcon={true}
                onClose={() => this.setState({lemmaDialogIndex: -1, existingLemma: lemmaPresent.unknown})}>
                <ModalContent>
                    <Grid>
                        <Grid.Row className="">
                            <Grid.Column width={8}>
                                <Input value={newLemma} fluid onChange={(e, {value}) => this.updateNewLemma(value)}/>
                            </Grid.Column>
                            <Grid.Column width={8}>
                                <Button icon="" type="button" color='blue'
                                    onClick={(e,d) => setLemma(lemmaDialogIndex, newLemma)}>{t('Set new lemma')}</Button>
                            </Grid.Column>
                        </Grid.Row>
                        { testingLemma || existingLemma !== lemmaPresent.unknown ? (
                            <Grid.Row className='compact'>
                                <Grid.Column width={16}>
                                { testingLemma ? <p className='testing-lemma'><Loader active inline size="mini"/>{t("Searching for lemma...")}</p>
                                : existingLemma === lemmaPresent.none ? <p className='no-lemma'><Icon name="warning sign"/>{t('New lemma')}</p>
                                : existingLemma === lemmaPresent.asLemma ? <p className='existing-lemma'><Icon name="check circle"/>{t('Existing lemma')}</p>
                                : existingLemma === lemmaPresent.asSublemma ? <p className='existing-sublemma'><Icon name="warning sign"/>{t('Existing sublemma')}</p>
                                : null }
                                </Grid.Column>
                            </Grid.Row>
                        ) : null }
                    </Grid>
                </ModalContent>
            </Modal>
            <Grid.Row className={item.created ? 'created' : ''}>
                <Grid.Column width={3}>
                    <div className={"pattern-checkbox at-top"/*+(item.original ? ' at-top' : '')*/}>
                    { toggleSelect ? 
                    <Checkbox checked={item.selected} onChange={() => toggleSelect(itemIdx)} disabled={item.created}/>
                    : null }
                    {errorMsg ?
                        <Icon name='exclamation triangle' color="red" title={errorMsg}/>
                    : null }
                    </div>
                    <div className="pattern-lemma">
                    { item.created ?
                        item.sublemma.map((sublemma: string) => <div><NavLink to={`/comp/cncdict/sublemma/${sublemma}`} target='_blank'>{sublemma}</NavLink></div>)
                    : item.original ?
                        item.sublemma.map((sublemma: string, i: number) => 
                            <div>
                                {sublemma} {lemma[i] && lemma[i]!==sublemma ? "\\"+lemma[i] : null }
                                <Button type="button" size="mini" color="blue" content="L" className="lemma-button"
                                    onClick={() => openLemmaDialog(i, lemma[i] || sublemma)}/>
                            </div>
                        )
                    :
                        item.sublemma.map((sublemma: string, i: number) => (
                            <div>
                                <Input value={sublemma} onChange={(e, {value}) => setSubLemma(itemIdx, i, value)} disabled={item.original || savingForms || item.created}
                                    actionPosition='left'
                                    action={{
                                        compact: true,
                                        size: 'tiny',
                                        content: item.sublemma[0].match(/^\p{Lu}/u) ? 'a' : 'A',
                                        onClick: () => setSubLemma(itemIdx, 0, (item.sublemma[i].match(/^\p{Lu}/u) ? item.sublemma[i].charAt(0).toLowerCase() : item.sublemma[i].charAt(0).toUpperCase()) + item.sublemma[i].slice(1))
                                    }}/>
                                    {lemma[i] && lemma[i]!==sublemma ? "\\"+lemma[i] : null }
                                    <Button type="button" size="mini" color="blue" content="L" className="lemma-button"
                                        onClick={() => openLemmaDialog(i, lemma[i] || sublemma)}/>
                            </div>
                            ))
                    }
                    </div>
                </Grid.Column>
                <Grid.Column width={5}>
                    { item.original ?
                        item.pattern.map((p: string, i: number) =>
                            <>
                            <div className="inline">{p}</div>
                            {filterCheckboxes[i]}
                            </>
                        )
                    :
                        item.pattern.map((p: string, i: number) => 
                            <>
                            <div>
                                <Dropdown fluid selection search value={p} options={patternOptions[i]} disabled={item.original || savingForms || item.created}
                                    onChange={(e, {value}) => setPattern(itemIdx, i, value as string)} placeholder={t('no pattern')}/>
                            </div>
                            {filterCheckboxes[i]}
                            </>
                            )
                    }
                    { homonyms === null ? null
                    : loadingHomonyms ? <p className='search_homonyms'><Loader active inline size="mini"/>{t("Searching for homonyms...")}</p>
                    : homonyms.length === 0 ? <p className='no_homonyms'><Icon name="check circle"/>{t('No homonymous forms')}</p>
                    : <p className='has_homonyms' title={homonyms.join(', ')}><Icon name="warning sign"/>{t('{{count}} homonymous forms', {count: homonyms.length})}</p>}
                </Grid.Column>
                { candidates ?
                <>
                <Grid.Column width={1}>
                    ({coverage})
                </Grid.Column>
                <Grid.Column width={5}>
                    {coveredForms.map(f => `${f.form} (${f.fq})`).join(', ')}
                </Grid.Column>
                <Grid.Column width={2} textAlign='right'>
                    <Button type="button" color='blue' onClick={() => saveForms(forms)}
                        disabled={savingForms || !forms.length || item.created || !itemActiveButton || !item.sublemma || !item.pattern} 
                        loading={savingForms} content={t('Create {{count}} forms', {count: forms.length})} icon={noneSelected ? "play" : "pause"} labelPosition='right'/>
                </Grid.Column>
                </>
                :
                <>
                <Grid.Column width={8} textAlign='right'>
                    <Button type="button" color='blue' onClick={() => saveForms(forms)}
                        disabled={savingForms || !forms.length || item.created || !itemActiveButton || !item.sublemma || !item.pattern} 
                        loading={savingForms} content={t('Create {{count}} forms', {count: forms.length})}/>
                </Grid.Column>
                </>
                }
            </Grid.Row>
            </>
        )
    }
}

export default withTranslation()(CNCDictCandidatesItem)